// The room socket client: room messages, commands, queries, and draw relay

import {
  RoomServerMessage,
  CommandMessage,
  QueryMessage,
  SubscribeMessage,
  UnsubscribeMessage,
  ClientInfoMessage,
  ClientInfoAckMessage,
  CommandAckMessage,
  QueryAckMessage,
  EventMessage,
  ErrorMessage,
  ConnectionStateMessage,
  QualityRequestMessage,
  QualityResponseMessage,
  WebSocketErrorType,
  RoomClientMessageType,
  RoomServerMessageType,
  ConnectionState,
  PlatformQuery,
  ClientInfo,
  DrawMessage,
  DrawSnapshotRequestMessage,
  DrawRelayMessage,
  DrawSnapshotMessage,
} from '@airhead-games/shared/types';
import { EventRelay } from './eventRelay';
import { ReconnectingSocket } from './reconnectingSocket';
import { createLogger } from '@airhead-games/shared/utilities';

const logger = createLogger('roomSocketClient');

type OutboundClientMessage =
  | CommandMessage
  | QueryMessage
  | SubscribeMessage
  | UnsubscribeMessage
  | ClientInfoMessage
  | QualityRequestMessage
  | DrawMessage
  | DrawSnapshotRequestMessage;

const getOutboundMessageDetails = (
  message: OutboundClientMessage
): { action?: string; raw: unknown } => {
  switch (message.type) {
    case RoomClientMessageType.COMMAND: {
      const { command } = message.payload;
      return {
        action:
          'action' in command && typeof command.action === 'string'
            ? command.action
            : undefined,
        raw: command,
      };
    }
    case RoomClientMessageType.QUERY:
      return { raw: message.payload.query };
    default:
      return { raw: message.payload };
  }
};

/**
 * Events emitted by RoomSocketClient
 */
export interface RoomSocketClientEvents {
  event: EventMessage['payload']; // Platform/game event from server
  commandAck: CommandAckMessage['payload']; // Command acknowledgement
  clientInfoAck: ClientInfoAckMessage['payload']; // Client info acknowledgement from server
  query_ack: QueryAckMessage['payload']; // Query acknowledgement from server
  error: ErrorMessage['payload']; // Error message
  connectionState: ConnectionStateMessage['payload'] & {
    code?: number;
    reason?: string;
  }; // Connection state changes (now includes close code/reason)
  connectionHealth: { healthy: boolean; lastPong: number | null }; // Ping/pong health status
  clientInfoComplete: undefined; // Client info has been acknowledged by server and connection is ready for communication
  serverMessage: RoomServerMessage; // Raw server message
  draw: DrawRelayMessage['payload']; // Relayed live-drawing stroke/control from the artist
  drawSnapshot: DrawSnapshotMessage['payload']; // Accumulated canvas for a (re)joining spectator
}
export interface RoomSocketClientOptions {
  maxReconnectAttempts?: number;
  reconnectDelay?: number;
  clientInfo: ClientInfo;
}
export class RoomSocketClient {
  private socket: ReconnectingSocket;
  private emitter = new EventRelay<RoomSocketClientEvents>();
  private clientInfoSent = false;
  private clientInfo: ClientInfo;

  on = this.emitter.on.bind(this.emitter);
  off = this.emitter.off.bind(this.emitter);

  constructor(options: RoomSocketClientOptions) {
    const reconnectDelay = options.reconnectDelay ?? 2000;
    this.clientInfo = options.clientInfo;
    this.socket = new ReconnectingSocket({
      channel: 'room',
      maxAttempts: options.maxReconnectAttempts ?? 5,
      retryDelay: (attempt) => reconnectDelay * attempt,
      shouldRetry: (code, reason) => !isNonRetryableRoomClose(code, reason),
    });
    this.socket.on('connectionState', (payload) => {
      if (payload.state === ConnectionState.DISCONNECTED) {
        this.clientInfoSent = false; // Reset client info flags on disconnect
      }
      this.emitter.emit('connectionState', payload);
    });
    this.socket.on('error', (payload) => this.emitter.emit('error', payload));
    this.socket.on('open', () => {
      // Send client info immediately after connection opens
      if (!this.clientInfoSent) {
        this.clientInfoSent = true;
        logger.debug('Connection ready, sending client info...');
        this.sendClientInfo();
      }
    });
    this.socket.on('message', (message) => this.handleMessage(message));
  }

  get ws(): WebSocket | undefined {
    return this.socket.ws;
  }

  get isManuallyClosed(): boolean {
    return this.socket.isManuallyClosed;
  }

  async connect(url: string) {
    this.clientInfoSent = false; // Reset client info flag for new connection
    this.socket.connect(url);
  }

  disconnect() {
    this.socket.disconnect();
  }

  sendCommand(command: CommandMessage['payload']['command']) {
    if (!this.socket.isOpen) {
      logger.debug('WebSocket not open, cannot send command');
      return;
    }
    logger.debug('Sending command', command.id, 'for', command.type, '...');
    this.send({
      type: RoomClientMessageType.COMMAND,
      payload: { command },
    } as CommandMessage);
  }

  sendQuery(query: PlatformQuery) {
    if (!this.socket.isOpen) {
      logger.debug('WebSocket not open, cannot send query');
      return;
    }
    logger.debug('Sending query', query.id, 'for', query.type, '...');
    this.send({
      type: RoomClientMessageType.QUERY,
      payload: { query },
    } as QueryMessage);
  }

  /**
   * Send an ephemeral live-drawing stroke/control. Fire-and-forget: no ack, and
   * silently dropped if the socket is not open (the next snapshot reconciles).
   */
  sendDraw(payload: DrawMessage['payload']) {
    if (!this.socket.isOpen) {
      return;
    }
    this.send({
      type: RoomClientMessageType.DRAW,
      payload,
    } as DrawMessage);
  }

  /** Request the accumulated canvas for a window (on join / reconnect). */
  sendDrawSnapshotRequest(payload: DrawSnapshotRequestMessage['payload']) {
    if (!this.socket.isOpen) {
      return;
    }
    this.send({
      type: RoomClientMessageType.DRAW_SNAPSHOT_REQUEST,
      payload,
    } as DrawSnapshotRequestMessage);
  }

  subscribe(eventTypes: string[]) {
    if (!this.socket.isOpen) {
      logger.debug('WebSocket not open, cannot subscribe');
      return;
    }
    this.send({
      type: RoomClientMessageType.SUBSCRIBE,
      payload: { eventTypes },
    } as SubscribeMessage);
  }

  unsubscribe(eventTypes: string[]) {
    if (!this.socket.isOpen) {
      logger.debug('WebSocket not open, cannot unsubscribe');
      return;
    }
    this.send({
      type: RoomClientMessageType.UNSUBSCRIBE,
      payload: { eventTypes },
    } as UnsubscribeMessage);
  }

  /**
   * Send a lightweight health check request to test connection quality (e.g. to test connection for latency)
   */
  triggerHealthCheck() {
    if (!this.socket.ws) {
      logger.debug('No WebSocket to check health');
      return;
    }

    if (this.socket.isOpen) {
      logger.debug('Sending health check via quality request...');
      this.send({
        type: RoomClientMessageType.QUALITY_REQUEST,
        payload: {},
      } as QualityRequestMessage);
    } else {
      logger.debug('WebSocket not open, health check cannot proceed');
    }
  }

  private sendClientInfo() {
    const clientInfo: ClientInfoMessage = {
      type: RoomClientMessageType.CLIENT_INFO,
      payload: {
        clientInfo: this.clientInfo,
      },
    };
    try {
      this.send(clientInfo);
      logger.debug(
        'Client info sent successfully, waiting for server acknowledgment...'
      );
      // clientInfoComplete will be set when we receive CLIENT_INFO_ACK from server
    } catch (error) {
      logger.error('Failed to send client info:', error);
      // Don't mark client info as complete if it failed
    }
  }

  private send(msg: OutboundClientMessage) {
    const json = JSON.stringify(msg);
    // Dev: emit outbound client messages for the event thread
    const kind = (() => {
      switch (msg.type) {
        case RoomClientMessageType.CLIENT_INFO:
          return 'handshake' as const;
        case RoomClientMessageType.COMMAND:
          return 'command' as const;
        case RoomClientMessageType.QUERY:
          return 'query' as const;
        case RoomClientMessageType.SUBSCRIBE:
        case RoomClientMessageType.UNSUBSCRIBE:
          return 'handshake' as const;
        default:
          return 'server' as const;
      }
    })();
    const { action, raw } = getOutboundMessageDetails(msg);
    this.socket.send(json);
    this.socket.pushDebugStream({
      direction: 'out',
      kind,
      type: msg.type,
      action,
      raw,
    });
    logger.debug('Message sent');
  }

  private handleMessage(message: unknown) {
    const msg = message as RoomServerMessage;

    // Validate message structure
    if (!msg || typeof msg !== 'object' || !msg.type) {
      logger.error('Received invalid message structure from server:', msg);
      this.emitter.emit('error', {
        errorType: WebSocketErrorType.INVALID_MESSAGE,
        message: 'Invalid message structure - missing type property',
        details: msg,
      });
      return;
    }

    this.emitter.emit('serverMessage', msg);
    switch (msg.type) {
      case RoomServerMessageType.EVENT: {
        const eventMsg = msg as EventMessage;
        if (
          !eventMsg.payload ||
          !eventMsg.payload.events ||
          !Array.isArray(eventMsg.payload.events)
        ) {
          logger.error(
            'Received EVENT message with invalid payload structure:',
            eventMsg
          );
          this.emitter.emit('error', {
            errorType: WebSocketErrorType.INVALID_MESSAGE,
            message: 'EVENT message missing payload.events array',
            details: eventMsg,
          });
          break;
        }

        const events = eventMsg.payload.events;
        if (events.length === 0) {
          logger.warn('Received EVENT message with empty events array');
          break;
        }

        logger.debug(
          `Processing event message with ${events.length} event(s): ${events.map((e) => e.type).join(', ')}`
        );

        // Log each event to event stream for debugging
        for (const ev of events) {
          this.socket.pushDebugStream({
            direction: 'in',
            kind: 'event',
            type: ev.type,
            version: ev.version,
            raw: ev,
          });
        }

        // Emit entire payload (with events array) to handler
        this.emitter.emit('event', eventMsg.payload);
        break;
      }
      case RoomServerMessageType.COMMAND_ACK: {
        logger.debug(`Processing command ack message...`);
        const ackMsg = msg as CommandAckMessage;
        if (!ackMsg.payload) {
          logger.error('Received COMMAND_ACK message with no payload:', ackMsg);
          this.emitter.emit('error', {
            errorType: WebSocketErrorType.INVALID_MESSAGE,
            message: 'COMMAND_ACK message missing payload',
            details: ackMsg,
          });
          break;
        }
        const ack = ackMsg.payload;
        this.socket.pushDebugStream({
          direction: 'in',
          kind: 'ack',
          type: 'COMMAND_ACK',
          action: ack.success ? 'success' : 'failure',
          raw: ack,
        });
        this.emitter.emit('commandAck', ack);
        break;
      }
      case RoomServerMessageType.QUERY_ACK: {
        logger.debug(`Processing query ack message...`);
        const queryAckMsg = msg as QueryAckMessage;
        if (!queryAckMsg.payload) {
          logger.error(
            'Received QUERY_ACK message with no payload:',
            queryAckMsg
          );
          this.emitter.emit('error', {
            errorType: WebSocketErrorType.INVALID_MESSAGE,
            message: 'QUERY_ACK message missing payload',
            details: queryAckMsg,
          });
          return;
        }
        // Add to event stream for debugging (same format as command acks)
        this.socket.pushDebugStream({
          direction: 'in',
          kind: 'ack',
          type: 'QUERY_ACK',
          action:
            queryAckMsg.payload.status === 'SUCCESS' ? 'success' : 'failure',
          raw: queryAckMsg.payload,
        });
        this.emitter.emit('query_ack', queryAckMsg.payload);
        break;
      }
      case RoomServerMessageType.CLIENT_INFO_ACK: {
        logger.debug(`Processing client info ack message...`);
        const clientInfoMsg = msg as ClientInfoAckMessage;
        if (!clientInfoMsg.payload) {
          logger.error(
            'Received CLIENT_INFO_ACK message with no payload:',
            clientInfoMsg
          );
          this.emitter.emit('error', {
            errorType: WebSocketErrorType.INVALID_MESSAGE,
            message: 'CLIENT_INFO_ACK message missing payload',
            details: clientInfoMsg,
          });
          break;
        }
        const clientInfoAck = clientInfoMsg.payload;
        this.socket.pushDebugStream({
          direction: 'in',
          kind: 'handshake',
          type: 'CLIENT_INFO_ACK',
          action: clientInfoAck.success ? 'success' : 'failure',
          raw: clientInfoAck,
        });
        this.emitter.emit('clientInfoAck', clientInfoAck);
        if (clientInfoAck.success) {
          logger.debug('Client info acknowledged by server, connection ready');
          this.emitter.emit('clientInfoComplete', undefined);
          this.socket.pushDebugStream({
            direction: 'in',
            kind: 'handshake',
            type: 'CLIENT_INFO_COMPLETE',
            raw: {},
          });
        } else {
          logger.error('Client info rejected by server:', clientInfoAck.error);
        }
        break;
      }
      case RoomServerMessageType.ERROR: {
        logger.warn(`Processing error message...`);
        const errMsg = msg as ErrorMessage;
        if (!errMsg.payload) {
          logger.error('Received ERROR message with no payload:', errMsg);
          // Don't emit error for invalid error message to avoid recursion
          break;
        }
        const errPayload = errMsg.payload;
        this.socket.pushDebugStream({
          direction: 'in',
          kind: 'error',
          type: 'ERROR',
          action: errPayload?.errorType || 'unknown',
          raw: errPayload,
        });
        this.emitter.emit('error', errPayload);
        break;
      }
      case RoomServerMessageType.CONNECTION_STATE: {
        logger.debug(`Processing connection state message...`);
        const connMsg = msg as ConnectionStateMessage;
        if (!connMsg.payload) {
          logger.error(
            'Received CONNECTION_STATE message with no payload:',
            connMsg
          );
          this.emitter.emit('error', {
            errorType: WebSocketErrorType.INVALID_MESSAGE,
            message: 'CONNECTION_STATE message missing payload',
            details: connMsg,
          });
          break;
        }
        const connPayload = connMsg.payload;
        this.socket.pushDebugStream({
          direction: 'in',
          kind: 'connection',
          type: 'CONNECTION_STATE',
          action: connPayload.state,
          raw: {
            state: connPayload.state,
            reason: connPayload.reason,
          },
        });
        this.emitter.emit('connectionState', connPayload);
        break;
      }
      case RoomServerMessageType.QUALITY_RESPONSE: {
        logger.debug(`Processing quality response message...`);
        const qualityMsg = msg as QualityResponseMessage;
        if (!qualityMsg.payload) {
          logger.error(
            'Received QUALITY_RESPONSE message with no payload:',
            qualityMsg
          );
          this.emitter.emit('error', {
            errorType: WebSocketErrorType.INVALID_MESSAGE,
            message: 'QUALITY_RESPONSE message missing payload',
            details: qualityMsg,
          });
          break;
        }
        const qualityPayload = qualityMsg.payload;
        this.socket.pushDebugStream({
          direction: 'in',
          kind: 'server',
          type: 'QUALITY_RESPONSE',
          action: 'health_check_response',
          raw: {
            timestamp: qualityPayload.timestamp,
            quality: qualityPayload.quality,
          },
        });
        logger.debug('Quality metrics received');
        break;
      }
      case RoomServerMessageType.DRAW: {
        const drawMsg = msg as DrawRelayMessage;
        if (!drawMsg.payload) {
          break;
        }
        this.emitter.emit('draw', drawMsg.payload);
        break;
      }
      case RoomServerMessageType.DRAW_SNAPSHOT: {
        const snapshotMsg = msg as DrawSnapshotMessage;
        if (!snapshotMsg.payload) {
          break;
        }
        this.emitter.emit('drawSnapshot', snapshotMsg.payload);
        break;
      }
    }
  }
}

// Room closes that retrying cannot fix
function isNonRetryableRoomClose(code: number, reason: string): boolean {
  // Close code 1003: Invalid data type (malformed request)
  if (code === 1003) {
    return true;
  }

  // Close code 1008: Policy violation (room not available, auth issues, etc.)
  if (code === 1008) {
    // These are errors that won't resolve with retries
    const nonRetryableReasons = [
      'Room not found',
      'Invalid room URL',
      'Authentication failed',
    ];
    return nonRetryableReasons.some((nonRetryableReason) =>
      reason?.includes(nonRetryableReason)
    );
  }

  // Close code 1000: Normal closure - don't retry
  if (code === 1000) {
    return true;
  }

  // Default to retryable for unknown codes or network issues
  return false;
}
